import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { CompetitionLifecycleAction } from '../components/CompetitionLifecycleControls';
import { TournamentByIdProvider, useActiveTournament } from '../state/TournamentContext';
import { setupStatus } from '../services/tournamentService';
import { paths, tournamentSlug } from '../lib/paths';
import { ErrorBoundary } from '../components/ErrorBoundary';

const TABS: Array<{ to: string; label: string; end?: boolean }> = [
  { to: '', label: 'Overview', end: true },
  { to: 'teams', label: 'Teams' },
  { to: 'schedule', label: 'Schedule' },
  { to: 'operations', label: 'Operations' },
  { to: 'scoring', label: 'Scoring' },
  { to: 'comms', label: 'Comms' },
  { to: 'wrap-up', label: 'Wrap-up' },
];

/**
 * Tournament workspace at `/t/:tournamentId`. The header pins the tournament
 * identity (name, dates, status, public-link) and the tab strip carries the
 * lifecycle (Overview · Teams · Schedule · Operations · Scoring · Comms ·
 * Wrap-up). Tabs are nested routes — each one renders an existing page or a
 * thin port of one. The `TournamentByIdProvider` resolves the tournament
 * from the URL so child pages can `useActiveTournament()` without re-picking.
 */
export function TournamentWorkspacePage() {
  return (
    <ErrorBoundary label="Event workspace">
      <TournamentByIdProvider>
        <WorkspaceChrome />
      </TournamentByIdProvider>
    </ErrorBoundary>
  );
}

function WorkspaceChrome() {
  const { tournament } = useActiveTournament();
  const navigate = useNavigate();
  const location = useLocation();
  const status = setupStatus(tournament);
  const isSeason = tournament.format === 'season';
  const slug = tournamentSlug(tournament);
  const canonicalBase = paths.adminCompetition(tournament.format, slug);
  const suffix = location.pathname.replace(/^\/admin\/(?:tournaments|seasons)\/[^/]+/, '');

  if (!location.pathname.startsWith(canonicalBase)) {
    return <Navigate to={`${canonicalBase}${suffix}${location.search}${location.hash}`} replace />;
  }

  return (
    <>
      <header className="workspace-header">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="eyebrow">{isSeason ? 'Season workspace' : 'Tournament workspace'}</div>
          <div className="workspace-header__title">{tournament.name}</div>
          <div className="workspace-header__sub">
            {new Date(tournament.startsAt).toLocaleDateString()} – {new Date(tournament.endsAt).toLocaleDateString()}
            {' · '}
            <span className={`chip chip--${tournament.status === 'in_progress' ? 'ok' : tournament.status === 'completed' ? 'info' : 'warn'}`}>
              <span className="chip__dot" />
              {tournament.status.replace('_', ' ')}
            </span>
            {' · '}Setup: <span className="muted">{status.label}</span>
          </div>
        </div>
        <div className="row" style={{ gap: 8, flexShrink: 0 }}>
          <CompetitionLifecycleAction
            tournament={tournament}
            onComplete={(action) => {
              if (action === 'delete') {
                navigate(isSeason ? paths.adminSeasons : paths.adminTournaments);
              }
            }}
          />
          <Link to={paths.event(tournament.slug)} className="btn btn--ghost">
            <ExternalLink size={14} /> Public page
          </Link>
        </div>
      </header>

      <nav className="workspace-tabs" aria-label="Workspace sections">
        {TABS.map((t) => (
          <NavLink
            key={t.label}
            to={t.to}
            end={t.end}
            className={({ isActive }) => `workspace-tabs__tab ${isActive ? 'is-active' : ''}`}
          >
            {t.label}
          </NavLink>
        ))}
      </nav>

      <ErrorBoundary label="Workspace tab">
        <Outlet />
      </ErrorBoundary>
    </>
  );
}
